/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { cloneDeep, escape } from 'lodash';

import { ButtonGroup, Dialog, TextField } from '@coursera/cds-core';
import type { FeedbackType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { FlagCategory } from 'bundles/authoring/course-content-feedback/types/courseContentFeedback';
import { create, createCmlContentDefinitionValueString, getInnerText, isEmpty } from 'bundles/cml';
import FlagCategories from 'bundles/content-feedback/components/cds/flag/FlagCategories';
import FlagContentButton from 'bundles/content-feedback/components/cds/flag/FlagContentButton';
import FlagContentNotification from 'bundles/content-feedback/components/cds/flag/FlagContentNotification';
import type { Comments } from 'bundles/content-feedback/components/cds/flag/types';
import type { ItemType } from 'bundles/content-feedback/constants/ItemTypes';
import { getCategoriesForLearnerItemType } from 'bundles/content-feedback/utils/ItemFeedbackUtils';
// eslint-disable-next-line @coursera/custom-rules/no-private-bundle-imports
import { useDubbingContext } from 'bundles/item-lecture/providers/DubbingProvider';
import type { Item } from 'bundles/learner-progress/types/Item';
import type { ComputedItemType } from 'bundles/ondemand/lib/ComputedItem';
import { useMemo } from 'bundles/page/lib/react-monkeypatch';

import _t from 'i18n!nls/content-feedback';

type Props = {
  computedItem: Item | ComputedItemType;
  itemFeedbackType: ItemType;

  comments: Comments;
  onSelect: () => void;
  onSubmit: (comments: Comments) => void;
  onRemove: (comments: Comments) => void;
  selected: boolean;
  onCancel?: () => void;
};

const styles = {
  root: css`
    display: inline-block;
  `,
  textfield: css`
    label {
      margin-bottom: 0 !important;
    }
  `,
};

const FlagContent = ({
  comments,
  itemFeedbackType,
  selected,
  onSelect,
  onSubmit,
  onRemove,
  onCancel,
  computedItem,
}: Props) => {
  const track = useTracker();
  const { isDubbingAvailable } = useDubbingContext();
  const categories = getCategoriesForLearnerItemType(itemFeedbackType, isDubbingAvailable);

  const currentCategory = useMemo(
    () => categories.find((value) => !!comments[value.id]) ?? categories[0],
    [categories, comments]
  );
  const currentComment = comments[currentCategory.id];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [showNotification, setShowNotification] = React.useState(false);
  const [notification, setNotification] = React.useState<string>('');
  const [selectedCategory, setSelectedCategory] = React.useState<FlagCategory>(currentCategory);
  const [text, setText] = React.useState(getInnerText(currentComment));
  const [commentExists, setCommentExists] = React.useState(!isEmpty(currentComment));

  const handleOpenDialog = () => {
    const category = categories.find((value) => !!comments[value.id]) ?? categories[0];
    const comment = comments[category.id];
    setSelectedCategory(category);
    setText(getInnerText(comment));
    setCommentExists(!isEmpty(comment));
    setDialogOpen(true);

    onSelect();
  };

  const handleSend = () => {
    const commentsClone = cloneDeep(comments);

    const cmlContent = createCmlContentDefinitionValueString(escape(text));

    commentsClone[selectedCategory.id] = create(cmlContent);

    setDialogOpen(false);
    setShowNotification(true);
    setNotification(_t('Your report has been submitted.'));

    onSubmit(commentsClone);
    track('send_content_feedback_report', {
      feedbackAction: 'submit',
      feedbackType: (selectedCategory.id as FeedbackType) || 'other',
    });
  };

  const handleRemove = () => {
    const commentsClone = cloneDeep(comments);
    delete commentsClone[selectedCategory.id];

    setDialogOpen(false);
    setShowNotification(true);
    setNotification(_t('Your report has been removed.'));

    onRemove(commentsClone);
    track('send_content_feedback_report', {
      feedbackAction: 'remove',
      feedbackType: (selectedCategory.id as FeedbackType) || 'other',
    });
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setShowNotification(false);
    onCancel?.();
    track('send_content_feedback_report', { feedbackAction: 'cancel' });
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleCategoryChange = (id: string) => {
    const category = categories.find((value) => value.id === id);
    if (!category) {
      return;
    }

    const comment = comments[category.id];

    setText(getInnerText(comment));
    setCommentExists(!isEmpty(comment));
    setSelectedCategory(category);
  };

  const handleTextChange = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(value);
  };

  const renderDialogContent = () => {
    const { CustomComponent } = selectedCategory;
    if (CustomComponent) {
      return <CustomComponent computedItem={computedItem as Item} onSend={handleSend} onCancel={handleCancel} />;
    }

    return (
      <TextField
        label={_t('Describe the issue')}
        value={text}
        onChange={handleTextChange}
        placeholder={selectedCategory.example ?? undefined}
        multiline
        fullWidth
        minRows={4}
        maxRows={8}
        css={styles.textfield}
      />
    );
  };

  const shouldRenderDialogActions = !selectedCategory.CustomComponent;

  return (
    <div className="rc-FlagContent" css={styles.root}>
      <FlagContentButton selected={selected} onClick={handleOpenDialog} />

      <Dialog
        open={dialogOpen}
        className="c-flag-categories-modal"
        aria-labelledby="report-problem-id"
        onClose={handleCancel}
      >
        <Dialog.HeadingGroup id="report-problem-id">{_t('Report an issue')}</Dialog.HeadingGroup>
        <Dialog.Content>
          <FlagCategories category={selectedCategory} categories={categories} onCategoryChange={handleCategoryChange} />
          {renderDialogContent()}
        </Dialog.Content>

        {shouldRenderDialogActions && (
          <Dialog.Actions>
            <ButtonGroup
              primaryButtonProps={{ disabled: !text, onClick: handleSend, children: _t('Submit') }}
              alternateButtonProps={{
                variant: 'secondary',
                onClick: commentExists ? handleRemove : handleCancel,
                children: commentExists ? _t('Remove') : _t('Cancel'),
              }}
            />
          </Dialog.Actions>
        )}
      </Dialog>

      <FlagContentNotification show={showNotification} message={notification} onCancel={handleCloseNotification} />
    </div>
  );
};

export default FlagContent;
